/**
 * ตำนานกระบี่ทั้งเก้า (the legend of the nine swords): the story behind the stone at the heart of
 * the world. A swordsman who never lost drove the nine swords of his life into a stone and sealed
 * a tenth beneath them; the player is one of those the stone woke to bear the ninth. The legend is
 * read chapter by chapter in the help window (H): the prologue from the start, each next chapter
 * as Elder Liu's quests are handed in. The full story bible is docs/LORE.md.
 */
import { QUESTS } from './quests';
import type { ZoneId } from './zones';

export interface SwordDef {
  /** Place in the stone, 1–9 (also the order of his life). */
  readonly n: number;
  readonly name: string;
  /** Where the sword lies now. */
  readonly zone: ZoneId;
  /** What it taught him, in a line. */
  readonly lesson: string;
}

export const NINE_SWORDS: readonly SwordDef[] = [
  {
    n: 1,
    name: 'กระบี่ไผ่',
    zone: 'yanyu', // zone id kept (parked map); canon place is the shrine of หมู่บ้านจวงซี (docs/LORE.md)
    lesson: 'คลื่นซัดเข้า คลื่นถอยกลับ คลื่นแตกฝั่ง รากของสามกระบวนท่า',
  },
  { n: 2, name: 'กระบี่คมโลหิต', zone: 'cinnabar', lesson: 'คมที่ไม่รู้จักหยุด ย่อมกินเจ้าของ' },
  { n: 3, name: 'กระบี่จันทร์เสน่หา', zone: 'dim-moon', lesson: 'อย่าชักกระบี่ก่อนเห็นใจคน' },
  { n: 4, name: 'กระบี่ระฆังเหล็ก', zone: 'bell-stone', lesson: 'แรงแท้อยู่ที่ลมหายใจ ไม่ได้อยู่ที่เหล็ก' },
  { n: 5, name: 'กระบี่สนหิมะ', zone: 'snow', lesson: 'พายุหิมะไม่เคยชนะต้นสนที่รอเป็น' },
  { n: 6, name: 'กระบี่ลายเมฆา', zone: 'mist-peak', lesson: 'ลมไม่เคยทำลายเมฆ เมฆไม่เคยต้านลม' },
  { n: 7, name: 'กระบี่พู่กัน', zone: 'pine-ink', lesson: 'หนึ่งขีดที่แม่น ดีกว่าร้อยขีดที่เร็ว' },
  { n: 8, name: 'กระบี่ปลาบิน', zone: 'lantern-road', lesson: 'กฎที่ไร้ใจ คือกระบี่ที่ไร้ด้าม' },
  { n: 9, name: 'กระบี่ร่องว่าง', zone: 'plains', lesson: 'กระบี่ที่เขาไม่เคยมี' },
];

export interface LegendChapter {
  readonly title: string;
  readonly text: readonly string[];
  /**
   * The quest that opens it; the prologue has none. The numbered books open in their own order: the Nth of their quests
   * handed in, in the order of the chain in force, opens the Nth book (the 256 m plains run those quests in another
   * order than the elder's chain, `legendChapters`).
   */
  readonly after?: string;
  /** Opens with its own quest only, outside the books' count: its text must not come before that fight (the reveal). */
  readonly ownQuest?: boolean;
}

export const LEGEND: readonly LegendChapter[] = [
  {
    title: 'บทนำ · ศิลาเก้ากระบี่',
    text: [
      'กลางลานศิลามีหินดำก้อนหนึ่ง ผิวหินมีร่องกระบี่เก้าร่อง แปดร่องว่างเปล่ามาหลายชั่วคน ร่องที่เก้าไม่เคยมีกระบี่ปักอยู่เลย',
      'ผู้เฒ่าเล่าต่อกันมาว่า เมื่อสามร้อยปีก่อน จอมกระบี่ผู้หนึ่งไม่เคยพ่ายแพ้แก่ผู้ใด ยุทธภพเรียกเขาว่าเถาฉางเยี่ย ผู้ยืนลำพังตลอดคืนอันยาวนาน ' +
        'ก่อนสิ้นลม เขาปักกระบี่เก้าเล่มแห่งชีวิตลงในหินนี้ และผนึกกระบี่เล่มที่สิบไว้ข้างใต้',
      'ศิลาเงียบงันมานานจนผู้คนลืม จนกระทั่งปีนี้ ศิลากลับร้อนดุจเลือด และเลือกคนแปลกหน้าให้กรีดฝ่ามือลงนามสัญญา เจ้าคือหนึ่งในนั้น',
    ],
  },
  {
    title: 'เล่มที่หนึ่ง · กระบี่ไผ่',
    after: 'three-forms',
    text: [
      'เถาฉางเยี่ยเกิดในหมู่บ้านชาวประมงจวงซีริมทะเลใต้ เด็กชายเหลาไม้ไผ่เป็นกระบี่ แล้วยืนฟันคลื่นอยู่ทั้งวัน',
      'คลื่นซัดเข้า เขาถอยรับ คลื่นตั้งตัว เขาฟันทะลุ คลื่นถอยกลับ เขาไล่ตาม ชาวบ้านหัวเราะเยาะ ไม่มีใครรู้ว่าเด็กคนนั้นกำลังเรียนสามกระบวนท่าจากทะเล',
      'กระบี่ไผ่เล่มนั้นยังอยู่ในศาลเจ้าเล็กของหมู่บ้าน ชาวประมงจุดธูปให้ทุกครั้งก่อนออกเรือ',
    ],
  },
  {
    title: 'เล่มที่สอง · กระบี่คมโลหิต',
    after: 'bandits',
    text: [
      'หนุ่มเถาฉางเยี่ยออกจากหมู่บ้านพร้อมกระบี่เหล็กคมกริบ เขาปราบโจรทั้งภูเขา แล้วท้าประลองยอดฝีมือทุกสำนักที่ขวางหน้า ไม่มีใครยืนได้ถึงกระบวนท่าที่สาม',
      'ยิ่งชนะ กระบี่ยิ่งกระหาย จนวันหนึ่งเขามองดูเลือดที่ไหลลงร่องดาบแล้วจำไม่ได้ว่าคนผู้นั้นทำผิดอะไร',
      'เขาปักกระบี่คมโลหิตไว้บนผาแดงแล้วเดินจากมา ผู้ที่ดึงมันขึ้นได้ในภายหลังตั้งสำนักดาบชาดขึ้น และไม่เคยเชื่อคำเตือนที่สลักอยู่บนผา',
    ],
  },
  {
    title: 'เล่มที่สาม · กระบี่จันทร์เสน่หา',
    after: 'wolves',
    text: [
      'ในหุบเขาจันทร์มัว เขาพบสตรีผู้หนึ่งที่เล่นพิณใต้แสงจันทร์ นางมอบกระบี่บางเฉียบดุจแสงเดือนให้เขา และเป็นครั้งแรกที่เขาวางกระบี่ลงได้',
      'คืนหนึ่งมีผู้บุกรุกเข้ามาในความมืด เถาฉางเยี่ยฟันออกไปก่อนจะได้เห็นหน้า เมื่อจุดไฟขึ้น ผู้ที่ล้มลงคือพี่ชายของนางที่มาตามน้องกลับบ้าน',
      'หมาป่าหอนตลอดคืนนั้น เขาทิ้งกระบี่ไว้ข้างพิณที่ไม่มีใครเล่นอีก และสาบานว่าจะไม่ชักกระบี่จนกว่าจะเห็นใจของคนตรงหน้า',
    ],
  },
  {
    title: 'บทแทรก · ศิษย์ผู้เฝ้าศิลา',
    after: 'black-sword',
    ownQuest: true,
    text: [
      'ตระกูลหลิวเฝ้าศิลาเก้ากระบี่มาตั้งแต่เถาฉางเยี่ยสิ้นชีพ กระบี่แปดเล่มถูกทายาทแปดคนดึงออกไปทีละเล่ม และนำกลับไปยังแผ่นดินที่มันถือกำเนิด ทุกครั้งที่กระบี่ออกจากหิน ผนึกข้างใต้ก็อ่อนลง',
      'ยี่สิบปีก่อน ฮั่วอู๋ซิน ศิษย์เอกของผู้เฒ่าหลิว ได้ยินเสียงกระซิบจากใต้ศิลา มันเรียกชื่อเขา และสัญญาว่าเขาจะไม่มีวันพ่ายแพ้',
      'คืนหนึ่งฮั่วอู๋ซินขุดลงไปใต้ร่องที่เก้า แล้วหายไปพร้อมกระบี่สีดำ เมื่อเขาปรากฏตัวอีกครั้ง ผิวกายดำดุจหมึก ผู้คนเรียกเขาว่าปีศาจกระบี่ทมิฬ ' +
        'ผู้เฒ่าหลิวไม่เคยเล่าเรื่องนี้ให้ใครฟัง จนกระทั่งวันที่เจ้าปราบเขาลงได้',
    ],
  },
  {
    title: 'เล่มที่สี่ · กระบี่ระฆังเหล็ก',
    after: 'monks',
    text: [
      'หลังคืนในหุบเขา เถาฉางเยี่ยเดินขึ้นภูเขาพันบันไดและคุกเข่าหน้าประตูโรงหล่อหินระฆังสามวันสามคืน ช่างใหญ่ไม่ถามชื่อ เพียงยื่นกระบี่ที่หล่อจากเหล็กระฆังร้าวให้ ทื่อและหนักจนแทบยกไม่ขึ้น',
      'สิบปีที่เขาใช้กระบี่ระฆังนั้นผ่าฟืนและหาบน้ำ จนรู้ว่าแรงที่แท้จริงอยู่ที่ลมหายใจ เมื่อเขาเคาะมันกับหิน หินแตกดังเสียงระฆังโดยไม่มีใครเลือดตก',
      'กระบี่ระฆังเล่มนั้นยังวางอยู่ในโรงหล่อใหญ่ แต่โรงหล่อร้างหลังเขาถูกช่างนอกรีตยึดไว้ พวกมันอ้างว่าได้ยินเสียงกระบี่อีกเล่มเรียกจากทางเหนือ',
    ],
  },
  {
    title: 'เล่มที่ห้า · กระบี่สนหิมะ',
    after: 'bears',
    text: [
      'เขาขึ้นไปอยู่บนยอดเขาหิมะเพียงลำพัง มีกวางขาวเขาดำตัวหนึ่งเดินตามเป็นเพื่อน หมีและหมาป่าบนเขาเรียนรู้ที่จะหลีกทางให้ชายผู้นั่งนิ่งใต้ต้นสน',
      'ฤดูหนาวทั้งฤดูเขาเฝ้าดูพายุหักต้นไม้ใหญ่ทีละต้น มีแต่ต้นสนที่โอนตามแล้วตั้งขึ้นใหม่ เขาเรียนที่จะรอจังหวะ ไม่ใช่ไล่ตามมัน และตีด้ามกระบี่จากไม้สนต้นนั้น',
      'วันนี้สัตว์บนเขาคลุ้มคลั่งเพราะปราณมาร มีเพียงกวางเมฆหิมะที่ยังยืนเฝ้าหลุมหิมะซึ่งฝังกระบี่สนหิมะเล่มนั้นไว้ เหมือนยังรอใครบางคน',
    ],
  },
  {
    title: 'เล่มที่หก · กระบี่ลายเมฆา',
    after: 'jiangshi',
    text: [
      'เมื่อลงจากเขา เถาฉางเยี่ยไปเยือนยอดหมอกและประลองกับผู้เฒ่าอ่านลมผู้หนึ่งเจ็ดวันเจ็ดคืน ไม่มีผู้ใดชนะ ไม่มีผู้ใดแพ้ เป็นครั้งแรกในชีวิตที่เขาหัวเราะออกมา',
      'ผู้เฒ่าสอนเขาว่าเมฆไม่เคยต้านลม แต่ลมไม่เคยทำลายเมฆได้ ผู้เฒ่ามอบกระบี่ลายเมฆที่ไม่เคยลับคมให้เขา และเขาไม่เคยใช้มันฆ่าใครอีกเลย',
      'กระบี่ลายเมฆาถูกเก็บไว้ในหอใหญ่ของยอดหมอก ผนึกของมันคุ้มสุสานใต้เขามาหลายร้อยปี จนเมื่อปราณจากกระบี่เล่มที่สิบปลุกศพให้ลุกขึ้น',
    ],
  },
  {
    title: 'เล่มที่เจ็ดและแปด · กระบี่พู่กันและกระบี่ปลาบิน',
    after: 'shadows',
    text: [
      'ในราชธานี เขาพบคนสองคน ผู้จดบันทึกผู้เขียนอักษรเดียวทั้งคืนจนกว่ามันจะสมบูรณ์ และผู้คุมทางผู้ไม่เคยฝ่าฝืนคำสั่งแม้สักครั้ง',
      'จากผู้จดบันทึก เขาเรียนว่าหนึ่งขีดที่แม่นดีกว่าร้อยขีดที่เร็ว จึงมอบกระบี่ที่ด้ามทำจากพู่กันไว้ให้หอหมึกสน ' +
        'จากผู้คุมทาง เขาเรียนว่ากฎที่ไร้ใจคือกระบี่ที่ไร้ด้าม ย่อมบาดมือผู้ถือ จึงมอบกระบี่ปักลายปลาบินไว้ให้กองทางโคมเหล็ก',
      'ทางโคมเหล็กยังสาบานต่อกระบี่เล่มนั้นทุกรุ่น ส่วนนักฆ่าเงาดำที่ซุ่มรอบค่ายกำลังหาทางขโมยมันไปถวายจอมมาร',
    ],
  },
  {
    title: 'เล่มที่เก้า · กระบี่ร่องว่าง',
    after: 'thousand-swords',
    text: [
      'ในวัยชรา เถาฉางเยี่ยกลับมายังลานศิลา ปักกระบี่แปดเล่มลงในหินทีละเล่ม เมื่อถึงร่องที่เก้า เขายกมือเปล่าขึ้นแล้ววางลงบนหิน ' +
        'ร่องที่เก้าไม่มีกระบี่ เขาไม่เคยรู้ว่ากระบี่เล่มนั้นหน้าตาเป็นอย่างไร จึงเว้นร่องไว้ให้ผู้ที่ลงนามด้วยเลือด และเพลงกระบี่ที่เจ้าใช้อยู่คือรอยแรกของมัน',
      'แต่ยังมีกระบี่อีกเล่มที่เขาทิ้งไม่ได้ เล่มที่สิบสีดำสนิท ไม่มีใครเห็นเขาตีมันขึ้นมา มันอยู่กับเขาตั้งแต่ชัยชนะครั้งแรก และหนักขึ้นทุกครั้งที่ไม่มีใครเอาชนะเขาได้ ' +
        'เขาผนึกมันไว้ใต้ศิลา ใช้กระบี่เก้าเล่มเป็นกุญแจเก้าดอก',
      'บัดนี้กระบี่เล่มที่สิบกลืนกระบี่ของจอมยุทธ์ทุกคนที่มันสังหารจนกลายเป็นจอมมารพันกระบี่ ทุกครั้งที่ถูกปราบ มันแตกออกแล้วรวมตัวใหม่ ' +
        'ผู้เฒ่าหลิวเชื่อว่ามันไม่ได้ต้องการชัยชนะ มันกำลังรอใครสักคน แต่ว่ารอใคร และเพื่ออะไร นั่นคือความลับที่ศิลายังไม่ยอมบอก',
    ],
  },
];

/** Chapters the player may read, given how far the elder's quest chain has come. */
export function legendChapters(questIndex: number): LegendChapter[] {
  const done = new Set(QUESTS.slice(0, questIndex).map((q) => q.id));
  // How many of the books' quests are handed in, whatever their order in the chain in force.
  const bookQuests = new Set(LEGEND.filter((c) => c.after && !c.ownQuest).map((c) => c.after!));
  let books = [...bookQuests].filter((id) => done.has(id)).length;
  return LEGEND.filter((c) => {
    if (!c.after) return true;
    if (c.ownQuest) return done.has(c.after);
    return books-- > 0;
  });
}

/**
 * The Wave Mask pirates (โจรสลัดหน้ากากคลื่น), the year-1 branch of the sect (docs/LORE.md "นิกายไร้พ่ายและกุญแจเก้าดอก"):
 * the names the game shows. Their chief and his shard of the thousand swords stay for chapter 9; the sect's name is
 * never said in game text before chapter 4. The dungeon (data/dungeon.ts) uses these names; dungeon.test.ts checks.
 */
export const WAVE_MASK = {
  band: 'โจรสลัดหน้ากากคลื่น',
  /** Their sea-cave hideout under the shrine cliff of จวงซี (the coastal shrine village; was Yanyu before 2026-10-11), and its vault. */
  hideout: 'ถ้ำคลื่นคำราม',
  vault: 'ห้องสมบัติเรือจม',
  /** The chief's quartermaster, keeper of the stolen goods (沙天): the dungeon's boss. */
  quartermaster: 'ฉลามเหล็ก ซาเทียน',
  /** The drill master of the whirlpool yard: the mini-boss. */
  drillMaster: 'เจ้าคลื่นเงิน',
} as const;
